/**
 * One Line Calendar - Core (utils, storage, API)
 * Single file for shared logic. No Liquid syntax.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'hanalei_booking_preferences';
  var STORAGE_DURATION = 60 * 60 * 1000;

  var utils = {
    formatDate: function(d) {
      if (!d) return null;
      return (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    },
    formatMoney: function(n) {
      return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDateISO: function(d) {
      var m = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return d.getFullYear() + '-' + m + '-' + day;
    },
    getMonthKey: function(y, m) { return y + '-' + String(m + 1).padStart(2, '0'); },
    getMonthName: function(m) {
      return ['January','February','March','April','May','June','July','August','September','October','November','December'][m];
    },
    getDaysInMonth: function(y, m) { return new Date(y, m + 1, 0).getDate(); },
    getFirstDayOfMonth: function(y, m) { return new Date(y, m, 1).getDay(); },
    isDatePast: function(d) {
      var t = new Date(); t.setHours(0,0,0,0);
      return d < t;
    },
    isDateInRange: function(d, ci, co) {
      return ci && co && d > ci && d < co;
    },
    isSameDate: function(a, b) {
      return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    },
    getNightsBetween: function(ci, co) {
      if (!ci || !co) return 0;
      return Math.ceil((co.getTime() - ci.getTime()) / (1000 * 60 * 60 * 24));
    }
  };

  var storage = {
    save: function(data) {
      if (!data || !data.checkinDate || !data.checkoutDate) return;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          checkinDate: data.checkinDate.toISOString(),
          checkoutDate: data.checkoutDate.toISOString(),
          guests: data.guests,
          timestamp: Date.now()
        }));
      } catch (e) { console.warn('Booking prefs save failed:', e); }
    },
    load: function() {
      try {
        var s = localStorage.getItem(STORAGE_KEY);
        if (!s) return null;
        var p = JSON.parse(s);
        if (Date.now() - p.timestamp > STORAGE_DURATION) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return p.checkinDate && p.checkoutDate && p.guests ? p : null;
      } catch (e) {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
        return null;
      }
    },
    clear: function() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }
  };

  var apiCache = {};
  var apiCleaningFee = {};
  var apiLoadingCounts = {};
  var loadedFrom = {};
  var loadedUntil = {};

  function cacheKey(listingId, year, month) {
    return (listingId || '') + '_' + utils.getMonthKey(year, month);
  }

  function parseMonthKey(key) {
    if (!key) return null;
    var parts = key.split('-');
    if (parts.length !== 2) return null;
    return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) - 1 };
  }

  var api = {
    cache: apiCache,
    fetchRange: function(base, listingId, startYear, startMonth, count) {
      if (!listingId || !base) return Promise.resolve();
      var startMonthKey = utils.getMonthKey(startYear, startMonth);
      var url = base + '/api/calendar?listingId=' + encodeURIComponent(listingId) +
        '&startMonth=' + startMonthKey + '&months=' + (count || 6);
      apiLoadingCounts[listingId] = (apiLoadingCounts[listingId] || 0) + 1;
      return fetch(url)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.success && Array.isArray(data.days)) {
            var byMonth = {};
            data.days.forEach(function(d) {
              if (!d.date || d.date.length < 7) return;
              var y = parseInt(d.date.slice(0, 4), 10);
              var m = parseInt(d.date.slice(5, 7), 10) - 1;
              var monthKey = utils.getMonthKey(y, m);
              if (!byMonth[monthKey]) byMonth[monthKey] = {};
              byMonth[monthKey][d.date] = { available: d.available, price: d.price, minNights: d.minNights };
            });
            Object.keys(byMonth).forEach(function(monthKey) {
              apiCache[(listingId || '') + '_' + monthKey] = byMonth[monthKey];
            });
            var parsed = parseMonthKey(data.startMonth);
            if (parsed) {
              var endMonthIdx = parsed.month + (data.months || 1) - 1;
              var endYear = parsed.year + Math.floor(endMonthIdx / 12);
              endMonthIdx = ((endMonthIdx % 12) + 12) % 12;
              var newEnd = utils.getMonthKey(endYear, endMonthIdx);
              if (!loadedFrom[listingId] || data.startMonth < loadedFrom[listingId]) {
                loadedFrom[listingId] = data.startMonth;
              }
              if (!loadedUntil[listingId] || newEnd > loadedUntil[listingId]) {
                loadedUntil[listingId] = newEnd;
              }
            }
          }
        })
        .catch(function(e) { console.warn('Calendar fetch failed:', startMonthKey, e); })
        .then(function() { apiLoadingCounts[listingId] = Math.max(0, (apiLoadingCounts[listingId] || 1) - 1); });
    },
    fetchCleaningFee: function(base, listingId) {
      if (!listingId || !base) return Promise.resolve();
      return fetch(base + '/api/calendar/cleaning-fee?listingId=' + encodeURIComponent(listingId))
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (typeof data.cleaningFee === 'number') {
            apiCleaningFee[listingId] = data.cleaningFee;
          }
        })
        .catch(function(e) { console.warn('Cleaning fee fetch failed:', e); });
    },
    clearAll: function(listingId) {
      if (listingId) {
        var keys = Object.keys(apiCache).filter(function(k) { return k.indexOf(listingId + '_') === 0; });
        keys.forEach(function(k) { delete apiCache[k]; });
        delete apiCleaningFee[listingId];
        delete loadedFrom[listingId];
        delete loadedUntil[listingId];
        delete apiLoadingCounts[listingId];
      } else {
        for (var k in apiCache) { delete apiCache[k]; }
        for (var k in apiCleaningFee) { delete apiCleaningFee[k]; }
        for (var k in loadedFrom) { delete loadedFrom[k]; }
        for (var k in loadedUntil) { delete loadedUntil[k]; }
        for (var k in apiLoadingCounts) { delete apiLoadingCounts[k]; }
      }
    },
    isMonthLoaded: function(year, month, listingId) {
      var key = (listingId || '') + '_' + utils.getMonthKey(year, month);
      return !!apiCache[key];
    },
    needsFetch: function(year, month, listingId, direction) {
      if (!listingId) return false;
      var from = loadedFrom[listingId];
      var until = loadedUntil[listingId];
      if (!from || !until) return true;
      var target = utils.getMonthKey(year, month);
      if (direction === 'prev') return target < from;
      if (direction === 'next') return target > until;
      return target < from || target > until;
    },
    isDateUnavailable: function(date, listingId) {
      var key = cacheKey(listingId, date.getFullYear(), date.getMonth());
      var m = apiCache[key];
      if (!m) return false;
      var ds = utils.formatDateISO(date);
      return !m[ds] ? true : !m[ds].available;
    },
    isNightAvailableInCache: function(date, listingId) {
      if (!listingId) return false;
      var key = cacheKey(listingId, date.getFullYear(), date.getMonth());
      var m = apiCache[key];
      if (!m) return false;
      var ds = utils.formatDateISO(date);
      var entry = m[ds];
      if (!entry) return false;
      return !!entry.available;
    },
    getDayPrice: function(date, fallback, listingId) {
      var key = cacheKey(listingId, date.getFullYear(), date.getMonth());
      var m = apiCache[key];
      if (m) {
        var ds = utils.formatDateISO(date);
        if (m[ds] && typeof m[ds].price === 'number') return m[ds].price;
      }
      return fallback;
    },
    getCleaningFee: function(listingId) {
      return listingId && apiCleaningFee[listingId] !== undefined ? apiCleaningFee[listingId] : 0;
    },
    isConfigured: function(lid, base) { return !!(lid && base); },
    isLoading: function(listingId) { return (apiLoadingCounts[listingId] || 0) > 0; }
  };

  window.HanaleiBooking = window.HanaleiBooking || {};
  window.HanaleiBooking.utils = utils;
  window.HanaleiBooking.storage = storage;
  window.HanaleiBooking.api = api;
})();
